import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from './SEOHead';
import Book3D from './ui/Book3D';
import CoverEditorDemo from './CoverEditorDemo';
import { resolveGenreToIndex } from '../lib/covers/resolve-genre';

const { FiArrowRight, FiArrowLeft, FiSmartphone, FiHeadphones, FiBook, FiImage, FiLayers, FiGrid } = FiIcons;

// Genre-specific cover palettes — these simulate what the AI cover engine produces per genre
const GENRES = [
  { label: 'Thriller', directions: [
    { name: 'Dark Tension', palette: { bg: 'linear-gradient(160deg,#0a0a0f,#1a1a2e)', accent: '#e63946', text: '#f1faee' }, img: '/img/cover-examples/dramatic-mood.jpg' },
    { name: 'Urban Noir', palette: { bg: 'linear-gradient(150deg,#0d1b2a,#1b263b)', accent: '#e9c46a', text: '#a8dadc' }, img: '/img/cover-examples/noir-mood.jpg' },
    { name: 'Cold Precision', palette: { bg: 'linear-gradient(160deg,#13181e,#21303f)', accent: '#90e0ef', text: '#caf0f8' }, img: '/img/cover-examples/cold-precision.jpg' },
  ]},
  { label: 'Romance', directions: [
    { name: 'Warm Bloom', palette: { bg: 'linear-gradient(160deg,#4a1942,#7b2d8b)', accent: '#f4a261', text: '#ffe8d6' }, img: '/img/cover-examples/warm-bloom.jpg' },
    { name: 'Rose Gold', palette: { bg: 'linear-gradient(150deg,#3d1a1a,#6b2d2d)', accent: '#e9967a', text: '#fce4d8' }, img: '/img/cover-examples/rose-gold.jpg' },
    { name: 'Garden Hour', palette: { bg: 'linear-gradient(160deg,#1e3a2f,#2d5a40)', accent: '#f9c74f', text: '#f0f7ee' }, img: '/img/cover-examples/garden-hour.jpg' },
  ]},
  { label: 'Memoir', directions: [
    { name: 'Honest & Raw', palette: { bg: 'linear-gradient(160deg,#3a2a1e,#5a4030)', accent: '#e6b87a', text: '#f0dcc2' }, img: '/img/cover-examples/warm-full.jpg' },
    { name: 'Still Water', palette: { bg: 'linear-gradient(150deg,#1e2d40,#2e4a5e)', accent: '#a8c5da', text: '#e8f4f8' }, img: '/img/cover-examples/still-water.jpg' },
    { name: 'Parchment', palette: { bg: 'linear-gradient(160deg,#f4f1ea,#e8e0d0)', accent: '#8b4513', text: '#2c1810' }, img: '/img/cover-examples/parchment.jpg' },
  ]},
  { label: 'Fantasy', directions: [
    { name: 'Epic Dark', palette: { bg: 'linear-gradient(160deg,#0f0622,#1a0a3d)', accent: '#c77dff', text: '#e0aaff' }, img: '/img/cover-examples/epic-dark.jpg' },
    { name: 'Forest Realm', palette: { bg: 'linear-gradient(150deg,#1b4332,#2d6a4f)', accent: '#95d5b2', text: '#d8f3dc' }, img: '/img/cover-examples/forest-realm.jpg' },
    { name: 'Fire & Gold', palette: { bg: 'linear-gradient(160deg,#2d1b00,#4a2e00)', accent: '#ffa500', text: '#fff3e0' }, img: '/img/cover-examples/fire-gold.jpg' },
  ]},
  { label: 'Business', directions: [
    { name: 'Bold Authority', palette: { bg: 'linear-gradient(160deg,#0b1d3a,#13294f)', accent: '#ff5a3c', text: '#ffd23f' }, img: '/img/cover-examples/bold-authority.jpg' },
    { name: 'Clean Leader', palette: { bg: '#f8f9fa', accent: '#1a3a4a', text: '#1a1a2e' }, img: '/img/cover-examples/clean-leader.jpg' },
    { name: 'Navy Trust', palette: { bg: 'linear-gradient(150deg,#1a237e,#283593)', accent: '#ffd600', text: '#ffffff' }, img: '/img/cover-examples/navy-trust.jpg' },
  ]},
  { label: 'Literary', directions: [
    { name: 'Minimalist', palette: { bg: 'linear-gradient(160deg,#1a1a2e,#0f0f1f)', accent: '#c9a227', text: '#e8c4a0' }, img: '/img/cover-examples/minimal-full.jpg' },
    { name: 'Type-Forward', palette: { bg: '#f4f1ea', accent: '#d8492b', text: '#1a3a4a' }, img: '/img/cover-examples/type-forward.jpg' },
    { name: 'Deep Prose', palette: { bg: 'linear-gradient(160deg,#12161b,#1e2b33)', accent: '#6fb3c9', text: '#cfe6ee' }, img: '/img/cover-examples/atmospheric-full.jpg' },
  ]},
];

// Mockup scene descriptors — pure CSS composition, no external images needed
const SCENES = [
  {
    id: 'desk',
    label: "Author's Desk",
    desc: 'Paperback + notebook, warm morning light',
    bg: 'linear-gradient(160deg, #e8dcc8 0%, #d4c4a8 100%)',
    accent: '#8b6914',
    angle: -18,
    width: 110,
    extra: 'notepad',
  },
  {
    id: 'kindle',
    label: 'Kindle Edition',
    desc: 'E-ink render at 2560×1600 sRGB',
    bg: '#1a1a1a',
    accent: '#4fc3f7',
    angle: 0,
    width: 90,
    frame: 'kindle',
  },
  {
    id: 'phone',
    label: 'Social Preview',
    desc: 'Instagram story — first impression on launch day',
    bg: 'linear-gradient(135deg,#667eea 0%,#764ba2 100%)',
    accent: '#fff',
    angle: 0,
    width: 72,
    frame: 'phone',
  },
  {
    id: 'hands',
    label: 'Reading Scene',
    desc: "Lifestyle shot — reader's hands, natural light",
    bg: 'linear-gradient(160deg,#f5f0e8 0%,#e8dcc8 100%)',
    accent: '#5a3e28',
    angle: -12,
    width: 130,
    extra: 'hands',
  },
  {
    id: 'shelf',
    label: 'Bookshelf',
    desc: 'Spine visible, shelved with bestsellers',
    bg: 'linear-gradient(160deg,#2c1810 0%,#3d2314 100%)',
    accent: '#c9a227',
    angle: 0,
    width: 100,
    extra: 'shelf',
  },
  {
    id: 'amazon',
    label: 'Amazon Listing',
    desc: 'Product shot — clean white, full 3D wrap',
    bg: '#ffffff',
    accent: '#ff9900',
    angle: -24,
    width: 120,
    extra: 'amazon',
  },
];

// A single cover direction rendered in a scene frame
const SceneCard = ({ scene, palette, title, author, coverImage }) => {
  const isKindle = scene.frame === 'kindle';
  const isPhone = scene.frame === 'phone';
  const isShelf = scene.extra === 'shelf';
  const isAmazon = scene.extra === 'amazon';
  const isHands = scene.extra === 'hands';

  return (
    <div className="rounded-2xl overflow-hidden shadow-lg border border-white/20" style={{ background: scene.bg, minHeight: 200 }}>
      <div className="relative flex items-center justify-center" style={{ minHeight: 200, padding: '20px 24px' }}>
        {/* Ambient grain overlay */}
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' opacity=\'1\'/%3E%3C/svg%3E")', backgroundSize: '80px' }} />

        {/* Kindle frame */}
        {isKindle && (
          <div className="relative" style={{ background: '#2a2a2a', borderRadius: 12, padding: '10px 8px 20px', width: 140, boxShadow: '0 8px 32px rgba(0,0,0,.6)' }}>
            <div style={{ background: '#111', borderRadius: 6, padding: 4 }}>
              <Book3D title={title} author={author} palette={palette} coverImage={coverImage} width={scene.width} angle={scene.angle} />
            </div>
            <div style={{ width: 24, height: 3, background: '#444', borderRadius: 2, margin: '8px auto 0' }} />
          </div>
        )}

        {/* Phone frame */}
        {isPhone && (
          <div style={{ background: '#111', borderRadius: 30, padding: '10px 5px', width: 120, boxShadow: '0 12px 40px rgba(0,0,0,.5)' }}>
            <div style={{ borderRadius: 22, overflow: 'hidden', background: palette.bg, padding: '12px 10px', textAlign: 'center' }}>
              <div style={{ color: '#fff', fontSize: 8, fontWeight: 700, opacity: .7, marginBottom: 6, letterSpacing: 1, textTransform: 'uppercase' }}>New Release</div>
              <Book3D title={title} author={author} palette={palette} coverImage={coverImage} width={scene.width} angle={scene.angle} />
              <div style={{ color: '#fff', fontSize: 7, opacity: .6, marginTop: 6 }}>Available now</div>
            </div>
          </div>
        )}

        {/* Shelf scene */}
        {isShelf && (
          <div className="flex items-end gap-1" style={{ height: 170 }}>
            {/* companion spines */}
            {[
              { bg: '#2d5a27', w: 18 }, { bg: '#4a2040', w: 22 }, { bg: '#1a3a4a', w: 16 },
            ].map((s, i) => (
              <div key={i} style={{ width: s.w, height: [140, 155, 130][i], background: s.bg, borderRadius: '2px 2px 0 0', boxShadow: 'inset 1px 0 3px rgba(255,255,255,.1)' }} />
            ))}
            {/* the book */}
            <div style={{ transform: 'rotateY(0deg)', flexShrink: 0 }}>
              <Book3D title={title} author={author} palette={palette} coverImage={coverImage} width={scene.width} angle={2} />
            </div>
            {[
              { bg: '#5a3e28', w: 20 }, { bg: '#1b263b', w: 25 },
            ].map((s, i) => (
              <div key={i} style={{ width: s.w, height: [145, 160][i], background: s.bg, borderRadius: '2px 2px 0 0', boxShadow: 'inset 1px 0 3px rgba(255,255,255,.1)' }} />
            ))}
            {/* shelf board */}
            <div className="absolute bottom-0 left-0 right-0 h-3" style={{ background: '#3d2314', boxShadow: '0 3px 8px rgba(0,0,0,.4)' }} />
          </div>
        )}

        {/* Amazon product shot */}
        {isAmazon && (
          <div style={{ textAlign: 'center' }}>
            <Book3D title={title} author={author} palette={palette} coverImage={coverImage} width={scene.width} angle={scene.angle} />
            <div style={{ marginTop: 10, fontSize: 9, color: '#111', fontWeight: 600 }}>{title}</div>
            <div style={{ fontSize: 8, color: '#888', marginTop: 2 }}>by {author}</div>
            <div style={{ marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 8, color: scene.accent }}>
              {'★★★★★'} <span style={{ color: '#555' }}>4.7 (382)</span>
            </div>
          </div>
        )}

        {/* Hands / reading scene */}
        {isHands && (
          <div style={{ position: 'relative' }}>
            {/* pseudo hands */}
            <div style={{ position: 'absolute', bottom: -8, left: '50%', transform: 'translateX(-50%)', width: 170, height: 35, background: 'linear-gradient(180deg,#c8956c,#b07d52)', borderRadius: '0 0 40px 40px', opacity: .7 }} />
            <Book3D title={title} author={author} palette={palette} coverImage={coverImage} width={scene.width} angle={scene.angle} />
          </div>
        )}

        {/* Desk / default */}
        {!isKindle && !isPhone && !isShelf && !isAmazon && !isHands && (
          <div style={{ position: 'relative' }}>
            {/* notepad shadow behind book */}
            <div style={{ position: 'absolute', top: 8, left: 30, width: 90, height: 120, background: '#fff', borderRadius: 4, transform: 'rotate(3deg)', boxShadow: '0 2px 8px rgba(0,0,0,.1)', opacity: .7 }}>
              {[...Array(6)].map((_, i) => (
                <div key={i} style={{ height: 1, background: '#ccc', margin: `${12 + i * 14}px 8px` }} />
              ))}
            </div>
            <div style={{ position: 'relative', zIndex: 1 }}>
              <Book3D title={title} author={author} palette={palette} coverImage={coverImage} width={scene.width} angle={scene.angle} />
            </div>
            {/* coffee mug hint */}
            <div style={{ position: 'absolute', right: -10, bottom: 10, width: 28, height: 32, border: `2px solid ${scene.accent}55`, borderRadius: '0 0 8px 8px', borderTop: 'none' }}>
              <div style={{ width: 10, height: 16, border: `2px solid ${scene.accent}55`, borderLeft: 'none', borderRadius: '0 50% 50% 0', position: 'absolute', right: -12, top: 6 }} />
            </div>
          </div>
        )}
      </div>
      <div style={{ background: 'rgba(0,0,0,.3)', backdropFilter: 'blur(4px)', padding: '8px 14px' }}>
        <div className="font-semibold text-white text-sm">{scene.label}</div>
        <div className="text-white/60 text-[11px]">{scene.desc}</div>
      </div>
    </div>
  );
};

const BOOK = { title: 'Echo', author: 'L. Hart' };

const CoverGalleryPage = () => {
  const [activeGenre, setActiveGenre] = useState(0);
  const [activeDir, setActiveDir] = useState(0);
  const [genreQuery, setGenreQuery] = useState('');
  const [genreMatch, setGenreMatch] = useState(null);
  const genre = GENRES[activeGenre];
  const direction = genre.directions[activeDir];

  const runGenreMatch = () => {
    const result = resolveGenreToIndex(genreQuery, GENRES);
    setActiveGenre(result.index);
    setActiveDir(0);
    setGenreMatch(result);
  };

  const structuredData = {
    '@context': 'https://schema.org', '@type': 'WebPage',
    name: 'Cover Showcase — Preview Your Book in 3D | ManuscriptSmith',
    description: 'See five pre-designed cover directions for your book rendered as real 3D books, in multiple presentation styles — standing, flat, paperback and social.',
    url: 'https://manuscriptsmith.com/cover-showcase',
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-parchment-50 to-parchment-100">
      <SEOHead
        title="Cover Showcase — Preview Your Book in Every Format | ManuscriptSmith"
        description="Preview genre-matched cover directions rendered as real 3D books — desk flat-lay, Kindle, social card, Amazon listing and more — then generate yours in minutes."
        keywords="3d book mockup, book cover preview, book cover generator, book cover lifestyle scenes, kdp cover preview, ai book cover"
        canonicalUrl="https://manuscriptsmith.com/cover-showcase"
        structuredData={structuredData}
      />
      <main>
        {/* Hero */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-10">
          <Link to="/cover-studio" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary-600 mb-8">
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4" /> Back to Cover Studio
          </Link>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-primary-700 bg-primary-50 px-3 py-1 rounded-full mb-5">
                <SafeIcon icon={FiImage} className="w-3.5 h-3.5" /> Cover Showcase
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                Your book,<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">
                  in every scene it'll live in.
                </span>
              </h1>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Cover Studio generates directions matched to your genre — then renders each one as a desk flat-lay,
                a Kindle, a social card, and an Amazon listing, so you see the full picture before you commit.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/signup" className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all shadow-lg shadow-primary-500/20">
                  Design my cover <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-2" />
                </Link>
                <Link to="/cover-studio" className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-white transition-all">
                  How it works
                </Link>
              </div>
            </div>
            <div className="flex justify-center lg:justify-end gap-6 items-end">
              <div style={{ transform: 'translateY(24px)' }}>
                <Book3D title={BOOK.title} author={BOOK.author} palette={GENRES[0].directions[0].palette} coverImage={GENRES[0].directions[0].img} width={110} angle={-28} />
              </div>
              <Book3D title={BOOK.title} author={BOOK.author} palette={GENRES[2].directions[0].palette} coverImage={GENRES[2].directions[0].img} width={150} angle={-22} />
              <div style={{ transform: 'translateY(16px)' }}>
                <Book3D title={BOOK.title} author={BOOK.author} palette={GENRES[5].directions[0].palette} coverImage={GENRES[5].directions[0].img} width={120} angle={-18} />
              </div>
            </div>
          </div>
        </section>

        {/* Genre selector + cover directions */}
        <section className="py-16 border-t border-parchment-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Covers scored for your genre</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">Select a genre to see how Cover Studio adapts — palette, mood, and commercial vs. literary positioning all shift.</p>
            </div>

            {/* Type your own genre — same keyword-match + fallback logic the real
                backend (style-agent.ts resolveGenre) uses on your actual manuscript. */}
            <div className="max-w-xl mx-auto mb-6">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  value={genreQuery}
                  onChange={(e) => setGenreQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && runGenreMatch()}
                  placeholder="Don't see your genre? Type it — e.g. &quot;cozy mystery&quot;, &quot;grimdark epic fantasy&quot;, &quot;YA dystopian&quot;"
                  maxLength={80}
                  className="flex-1 px-3 py-2 rounded-lg border border-parchment-300 text-sm focus:outline-none focus:border-primary-500"
                />
                <button type="button" onClick={runGenreMatch} disabled={!genreQuery.trim()}
                  className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg bg-gradient-to-r from-primary-500 to-secondary-500 text-white hover:from-primary-600 hover:to-secondary-600 disabled:opacity-50 whitespace-nowrap">
                  Match my genre
                </button>
              </div>
              {genreMatch && (
                <p className="text-[12px] text-gray-500 mt-1.5 text-center">
                  {genreMatch.matched
                    ? <>Matched to <span className="font-semibold text-primary-700">{genreMatch.label}</span> — closest real backend profile: <span className="italic">{genreMatch.backendProfile}</span>.</>
                    : <>No exact match for "{genreQuery}" — defaulted to <span className="font-semibold text-primary-700">Literary</span>, same as the real app does for an unrecognized genre.</>
                  }
                </p>
              )}
            </div>

            {/* Genre pill filter */}
            <div className="flex flex-wrap justify-center gap-2 mb-10">
              {GENRES.map((g, i) => (
                <button key={g.label} onClick={() => { setActiveGenre(i); setActiveDir(0); setGenreMatch(null); }}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all border ${activeGenre === i ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white border-transparent shadow-md' : 'bg-white text-gray-600 border-parchment-200 hover:border-primary-300 hover:text-primary-700'}`}>
                  {g.label}
                </button>
              ))}
            </div>

            {/* Cover direction selector */}
            <div className="grid grid-cols-3 gap-4 mb-10 max-w-xl mx-auto">
              {genre.directions.map((d, i) => (
                <button key={d.name} onClick={() => setActiveDir(i)}
                  className={`rounded-xl overflow-hidden border-2 transition-all bg-white shadow-sm ${activeDir === i ? 'border-primary-500 scale-[1.02]' : 'border-parchment-200 hover:border-primary-300'}`}>
                  {d.img ? (
                    <div className="h-14 bg-cover bg-center" style={{ backgroundImage: `url(${d.img})` }} />
                  ) : (
                    <div className="h-14" style={{ background: d.palette.bg }} />
                  )}
                  <div className="bg-white px-2 py-1.5">
                    <div className="text-[11px] font-semibold text-gray-700">{d.name}</div>
                  </div>
                </button>
              ))}
            </div>

            {/* Active cover — big 3D preview */}
            <div className="flex justify-center gap-8 items-end">
              <div style={{ transform: 'translateY(20px) scale(0.85)', opacity: 0.6 }}>
                <Book3D title={BOOK.title} author={BOOK.author}
                  palette={genre.directions[(activeDir + genre.directions.length - 1) % genre.directions.length].palette}
                  coverImage={genre.directions[(activeDir + genre.directions.length - 1) % genre.directions.length].img}
                  width={130} angle={-28} />
              </div>
              <div>
                <Book3D title={BOOK.title} author={BOOK.author} palette={direction.palette} coverImage={direction.img} width={200} angle={-22} />
              </div>
              <div style={{ transform: 'translateY(20px) scale(0.85)', opacity: 0.6 }}>
                <Book3D title={BOOK.title} author={BOOK.author}
                  palette={genre.directions[(activeDir + 1) % genre.directions.length].palette}
                  coverImage={genre.directions[(activeDir + 1) % genre.directions.length].img}
                  width={130} angle={-18} />
              </div>
            </div>
            <div className="text-center mt-4">
              <span className="text-primary-600 font-semibold">{direction.name}</span>
              <span className="text-gray-500 text-sm ml-2">— {genre.label}</span>
            </div>
          </div>
        </section>

        {/* Mockup scenes grid */}
        <section className="py-16 border-t border-parchment-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">One cover, every scene it'll appear in</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Approve one direction — Cover Studio renders it in every context you need, from Amazon listings to author-site lifestyle shots.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {SCENES.map((scene) => (
                <SceneCard key={scene.id} scene={scene} palette={direction.palette} coverImage={direction.img} title={BOOK.title} author={BOOK.author} />
              ))}
            </div>
          </div>
        </section>

        {/* Direct-edit demo — placement / typography / colors, live, same controls as the real app */}
        <section className="py-16 border-t border-parchment-200 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Change placement, type and color — directly</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">Move the title, swap the typeface, pick new colors — or just ask. Try it right here, live.</p>
            </div>
            <CoverEditorDemo />
          </div>
        </section>

        {/* Format export strip */}
        <section className="py-16 border-t border-parchment-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">KDP-ready exports, all formats</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">One approved cover exports in every dimension you need — print bleed, digital, audiobook square, social crops.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: FiBook, label: 'Paperback wrap', spec: 'Full bleed, 300 DPI, KDP geometry', color: 'from-primary-500 to-secondary-500' },
                { icon: FiSmartphone, label: 'Kindle / ebook', spec: '2560×1600, sRGB, cover-only', color: 'from-secondary-500 to-primary-600' },
                { icon: FiHeadphones, label: 'Audiobook', spec: '3000×3000 square, ACX + Findaway', color: 'from-amber-500 to-orange-600' },
                { icon: FiGrid, label: 'Social pack', spec: 'Instagram, Twitter banner, story', color: 'from-rose-500 to-pink-600' },
                { icon: FiLayers, label: 'Full wrap PDF', spec: 'Back + spine + front, print-ready', color: 'from-emerald-500 to-teal-600' },
                { icon: FiImage, label: 'Flat-lay PNG', spec: 'Cover only, transparent background', color: 'from-sky-500 to-indigo-600' },
              ].map((f) => (
                <div key={f.label} className="rounded-2xl border border-parchment-200 bg-white p-5 shadow-sm hover:shadow-md transition-all">
                  <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center mb-3 shadow`}>
                    <SafeIcon icon={f.icon} className="w-4 h-4 text-white" />
                  </div>
                  <div className="font-semibold text-gray-900 text-sm">{f.label}</div>
                  <div className="text-gray-500 text-[11px] mt-1">{f.spec}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="relative rounded-3xl overflow-hidden text-center p-12 bg-gradient-to-r from-primary-500 to-secondary-500 shadow-xl">
            <div className="absolute inset-0 opacity-15"
              style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, #ffffff 0%, transparent 50%), radial-gradient(circle at 20% 80%, #000000 0%, transparent 50%)' }} />
            <div className="relative z-10">
              <div className="flex justify-center gap-6 mb-8 items-end">
                {[GENRES[0].directions[0], GENRES[2].directions[0], GENRES[5].directions[0]].map((d, i) => (
                  <div key={i} style={{ transform: `translateY(${[8, 0, 12][i]}px) scale(${[0.85, 1, 0.8][i]})` }}>
                    <Book3D title={BOOK.title} author={BOOK.author} palette={d.palette} coverImage={d.img} width={[90, 120, 80][i]} angle={-22} />
                  </div>
                ))}
              </div>
              <h2 className="text-3xl font-bold text-white mb-3">Design your cover in 5 minutes</h2>
              <p className="text-white/70 mb-8 text-lg">ManuscriptSmith reads your manuscript, generates five genre-matched directions, and exports KDP-ready files.</p>
              <Link to="/signup"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 font-bold text-lg rounded-xl hover:bg-gray-50 transition-all shadow-2xl">
                Get started <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-2" />
              </Link>
              <div className="mt-4 text-white/40 text-sm">See plans and pricing</div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default CoverGalleryPage;
